import type { AxiosRequestConfig } from 'axios';

import { axiosInstance } from '../axios-instance';

type RequestOptions = AxiosRequestConfig | undefined;

export type BulkUploadExportFormat = 'csv' | 'xlsx';

export type BulkUploadMode = 'InitialImport' | 'ApprovalWorkflow';

export const BULK_UPLOAD_TYPE_USER_ROLE_ASSIGNMENT = 'UserRoleAssignment';

export interface BulkUploadRowResultWireDto {
  rowNumber: number;
  username: string;
  dbName: string;
  roleName: string;
  currentRoleName?: string | null;
  crossUserId?: string | null;
  targetRoleId?: number | null;
  outcome: string;
  errorMessage?: string | null;
}

export interface BulkUploadPreviewWireDto {
  previewId: string;
  uploadType: string;
  mode: string;
  contentHash: string;
  totalRows: number;
  assignCount: number;
  changeCount: number;
  noOpCount: number;
  invalidCount: number;
  canCommit: boolean;
  rows: BulkUploadRowResultWireDto[];
}

export interface BulkUploadCommitWireDto {
  previewId: string;
  uploadType: string;
  mode: string;
  successCount: number;
  noOpCount: number;
  failureCount: number;
  rows: BulkUploadRowResultWireDto[];
  approvalRequestIds: number[];
}

export interface BulkUploadCommitBody {
  previewId: string;
  uploadType: string;
  mode: string;
  justification: string;
  contentHash?: string;
}

/**
 * Omitted from published OpenAPI until TROC-244/245 BE is in the vendored spec.
 */
export const getApiUserManagementV1BulkUploadsExportUserRoleAssignment = (
  format: BulkUploadExportFormat = 'csv',
  options?: RequestOptions,
  signal?: AbortSignal
) =>
  axiosInstance<Blob>(
    {
      url: '/api/user-management/v1/bulk-uploads/export/user-role-assignment',
      method: 'GET',
      params: { format },
      responseType: 'blob',
      signal
    },
    options
  );

export const getApiUserManagementV1BulkUploadsTemplate = (
  uploadType: string,
  format: BulkUploadExportFormat = 'csv',
  options?: RequestOptions,
  signal?: AbortSignal
) =>
  axiosInstance<Blob>(
    {
      url: `/api/user-management/v1/bulk-uploads/templates/${encodeURIComponent(uploadType)}`,
      method: 'GET',
      params: { format },
      responseType: 'blob',
      signal
    },
    options
  );

export const postApiUserManagementV1BulkUploadsPreview = (
  formData: FormData,
  options?: RequestOptions,
  signal?: AbortSignal
) =>
  axiosInstance<BulkUploadPreviewWireDto>(
    {
      url: '/api/user-management/v1/bulk-uploads/preview',
      method: 'POST',
      data: formData,
      // Let the browser set multipart boundary — a bare Content-Type breaks ASP.NET binding (400).
      signal
    },
    options
  );

export const postApiUserManagementV1BulkUploadsCommit = (
  body: BulkUploadCommitBody,
  options?: RequestOptions,
  signal?: AbortSignal
) =>
  axiosInstance<BulkUploadCommitWireDto>(
    {
      url: '/api/user-management/v1/bulk-uploads/commit',
      method: 'POST',
      data: body,
      signal
    },
    options
  );

export const getApiUserManagementV1BulkUploadsErrors = (
  previewId: string,
  options?: RequestOptions,
  signal?: AbortSignal
) =>
  axiosInstance<Blob>(
    {
      url: `/api/user-management/v1/bulk-uploads/jobs/${encodeURIComponent(previewId)}/errors`,
      method: 'GET',
      responseType: 'blob',
      signal
    },
    options
  );
